<template>
  <v-container fill-height>
    <v-row align="center" justify="center" style="height: 90vh" dense>
      <v-col class="py-12">
        <v-card elevation="2" class="mx-auto" max-width="700">
          <v-card-title class="primary white--text">
            <v-icon dark left>mdi-cog</v-icon>
            Initial Setup
          </v-card-title>

          <v-stepper v-model="step" vertical class="elevation-0">
            <!-- Step 1: Mode Selection -->
            <v-stepper-step :complete="step > 1" step="1">
              Select Mode
              <small>Choose how this system will operate</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <v-radio-group v-model="appMode" class="ml-2">
                <v-radio value="master" class="mb-4">
                  <template v-slot:label>
                    <div>
                      <strong>Master Mode</strong>
                      <div class="text-caption grey--text">
                        This system will host the database. Other systems will connect to this one.
                      </div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="client">
                  <template v-slot:label>
                    <div>
                      <strong>Client Mode</strong>
                      <div class="text-caption grey--text">
                        Connect to another system that is running as Master.
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
              <div class="mt-4">
                <v-btn color="primary" @click="step = appMode === 'client' ? 2 : 3">
                  Continue
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 2: Master IP (only for Client mode) -->
            <v-stepper-step :complete="step > 2" step="2" v-if="appMode === 'client'">
              Enter Master IP
              <small>IP address of the Master system</small>
            </v-stepper-step>
            <v-stepper-content step="2" v-if="appMode === 'client'">
              <v-text-field
                v-model="masterIP"
                label="Master System IP Address"
                placeholder="192.168.1.10"
                :rules="[rules.required, rules.ip]"
                outlined
                dense
                class="mt-2"
              ></v-text-field>

              <v-btn
                color="secondary"
                @click="testConnection"
                :loading="testing"
                class="mr-2"
              >
                <v-icon left>mdi-connection</v-icon>
                Test Connection
              </v-btn>

              <v-alert v-if="testResult" :type="testResult.success ? 'success' : 'error'" class="mt-3" dense>
                {{ testResult.message }}
              </v-alert>

              <div class="mt-4">
                <v-btn text @click="step = 1">Back</v-btn>
                <v-btn
                  color="primary"
                  @click="step = 3"
                  :disabled="!testResult || !testResult.success"
                >
                  Continue
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 3: Confirmation -->
            <v-stepper-step :complete="step > 3" step="3">
              Confirm Setup
            </v-stepper-step>
            <v-stepper-content step="3">
              <v-alert type="info" outlined class="mb-4">
                <div v-if="appMode === 'master'">
                  <strong>Master Mode</strong><br>
                  This system will host the database.
                </div>
                <div v-else>
                  <strong>Client Mode</strong><br>
                  Connecting to: <strong>{{ masterIP }}</strong><br>
                  All data will come from the Master system.
                </div>
              </v-alert>

              <!-- Show IP addresses for Master mode -->
              <v-card v-if="appMode === 'master'" outlined class="mb-4">
                <v-card-title class="subtitle-1 pb-2">
                  <v-icon left color="primary">mdi-ip-network</v-icon>
                  Your IP Addresses
                </v-card-title>
                <v-card-text>
                  <div v-if="loadingIPs" class="text-center py-2">
                    <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
                    <span class="ml-2">Loading IP addresses...</span>
                  </div>
                  <div v-else-if="serverAddresses.length > 0">
                    <div v-for="addr in serverAddresses" :key="addr.address" class="mb-2">
                      <v-chip small outlined class="mr-2">{{ addr.interface }}</v-chip>
                      <strong class="primary--text" style="font-size: 1.1em;">{{ addr.address }}</strong>
                    </div>
                    <v-divider class="my-3"></v-divider>
                    <div class="text-caption grey--text">
                      <v-icon small class="mr-1">mdi-information</v-icon>
                      Tell other systems to use one of these IP addresses to connect as Client.
                      They will enter the IP in Client mode setup.
                    </div>
                  </div>
                  <div v-else class="text-caption grey--text">
                    No network interfaces found. Make sure you're connected to the ship's network.
                  </div>
                </v-card-text>
              </v-card>

              <div class="mt-4">
                <v-btn text @click="step = appMode === 'client' ? 2 : 1">Back</v-btn>
                <v-btn color="primary" @click="startDownload" :loading="saving">
                  <v-icon left>mdi-download</v-icon>
                  Download Course Content
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 4: Download Progress -->
            <v-stepper-step :complete="step > 4" step="4">
              Download Content
              <small>Downloading course materials for offline use</small>
            </v-stepper-step>
            <v-stepper-content step="4">
              <div class="text-center py-4">
                <v-icon size="64" :color="downloadComplete ? 'success' : 'primary'" class="mb-4">
                  {{ downloadComplete ? 'mdi-check-circle' : 'mdi-cloud-download' }}
                </v-icon>

                <h3 class="mb-2">
                  {{ downloadComplete ? 'Download Complete!' : 'Downloading Course Content...' }}
                </h3>

                <p class="text-caption grey--text mb-4">
                  {{ downloadStatus }}
                </p>

                <!-- Overall Progress -->
                <v-progress-linear
                  :value="overallProgress"
                  height="25"
                  rounded
                  :color="downloadComplete ? 'success' : 'primary'"
                  class="mb-2"
                >
                  <template v-slot:default>
                    <strong>{{ overallProgress }}%</strong>
                  </template>
                </v-progress-linear>

                <div class="text-caption mb-4">
                  {{ downloadedFiles }} / {{ totalFiles }} files
                  <span v-if="currentFileName" class="ml-2">({{ currentFileName }})</span>
                </div>

                <!-- Current File Progress -->
                <v-progress-linear
                  v-if="!downloadComplete && currentFileProgress > 0"
                  :value="currentFileProgress"
                  height="10"
                  rounded
                  color="secondary"
                  class="mb-4"
                ></v-progress-linear>

                <!-- Download Stats -->
                <v-row v-if="!downloadComplete" class="mt-4" justify="center">
                  <v-col cols="auto">
                    <v-chip small outlined color="info">
                      <v-icon left small>mdi-file-document</v-icon>
                      {{ downloadedMB }} MB downloaded
                    </v-chip>
                  </v-col>
                  <v-col cols="auto" v-if="downloadSpeed">
                    <v-chip small outlined color="success">
                      <v-icon left small>mdi-speedometer</v-icon>
                      {{ downloadSpeed }}
                    </v-chip>
                  </v-col>
                </v-row>

                <!-- Error Message -->
                <v-alert v-if="downloadError" type="error" class="mt-4" dense>
                  {{ downloadError }}
                  <v-btn small text @click="retryDownload" class="ml-2">Retry</v-btn>
                </v-alert>

                <!-- Complete Button -->
                <div class="mt-6" v-if="downloadComplete">
                  <v-btn color="success" large @click="completeSetup">
                    <v-icon left>mdi-check</v-icon>
                    Start Using App
                  </v-btn>
                </div>

                <!-- Skip Button (if download fails) -->
                <div class="mt-4" v-if="downloadError || downloadSkippable">
                  <v-btn text small @click="skipDownload">
                    Skip for now (limited offline features)
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "SetupWizard",
  data() {
    return {
      step: 1,
      appMode: "master",
      masterIP: "",
      masterPort: 8080,
      testing: false,
      testResult: null,
      saving: false,
      serverAddresses: [],
      loadingIPs: false,
      rules: {
        required: (v) => !!v || "Required",
        ip: (v) => /^(\d{1,3}\.){3}\d{1,3}$/.test(v) || "Invalid IP format",
      },
      // Download progress
      downloadComplete: false,
      downloadStatus: "Preparing to download...",
      overallProgress: 0,
      downloadedFiles: 0,
      totalFiles: 0,
      currentFileName: "",
      currentFileProgress: 0,
      downloadedMB: "0",
      downloadSpeed: "",
      downloadError: null,
      downloadSkippable: false,
      downloadStartTime: null,
      totalBytesDownloaded: 0,
      progressCleanup: null, // Cleanup function for progress listener
      completeCleanup: null, // Cleanup function for completion listener
    };
  },
  beforeDestroy() {
    // Clean up listeners to prevent memory leaks
    if (this.progressCleanup) {
      this.progressCleanup();
      this.progressCleanup = null;
    }
    if (this.completeCleanup) {
      this.completeCleanup();
      this.completeCleanup = null;
    }
    // Also use explicit cleanup as fallback
    if (window.electron && window.electron.removeDownloadProgressListener) {
      window.electron.removeDownloadProgressListener();
    }
    if (window.electron && window.electron.removeDownloadCompleteListener) {
      window.electron.removeDownloadCompleteListener();
    }
  },
  watch: {
    step(newStep) {
      // Load IP addresses when entering step 3 in master mode
      if (newStep === 3 && this.appMode === "master") {
        // Small delay to ensure IPC is fully ready (especially in production builds)
        setTimeout(() => this.loadServerIPs(), 100);
      }
    },
  },
  methods: {
    async loadServerIPs() {
      this.loadingIPs = true;
      this.serverAddresses = [];

      // Wait a moment for IPC to be fully ready in production
      await new Promise(resolve => setTimeout(resolve, 200));

      try {
        // Try IPC first (most reliable in Electron)
        if (window.electron && typeof window.electron.getNetworkInterfaces === 'function') {
          console.log("Trying to load network interfaces via IPC...");
          console.log("window.electron available:", !!window.electron);
          console.log("getNetworkInterfaces available:", typeof window.electron.getNetworkInterfaces);
          try {
            const result = await window.electron.getNetworkInterfaces();
            console.log("IPC getNetworkInterfaces result:", JSON.stringify(result));
            if (result && result.addresses && result.addresses.length > 0) {
              this.serverAddresses = result.addresses;
              console.log("Loaded network interfaces via IPC:", this.serverAddresses);
              this.loadingIPs = false;
              return;
            } else {
              console.warn("IPC returned empty or invalid addresses:", result);
            }
          } catch (ipcError) {
            console.warn("IPC getNetworkInterfaces failed:", ipcError);
            console.warn("IPC error details:", JSON.stringify(ipcError, Object.getOwnPropertyNames(ipcError)));
          }
        } else {
          console.warn("window.electron or getNetworkInterfaces not available");
          console.log("window.electron:", window.electron);
        }

        // Fallback: Try HTTP to Express server
        const portsToTry = [8080, 8081, 8082, 8083, 3080];

        // Try to get port from Electron IPC
        let expressPort = 8080;
        if (window.electron && window.electron.getExpressPort) {
          try {
            expressPort = await window.electron.getExpressPort();
            console.log("Got Express port from IPC:", expressPort);
          } catch (e) {
            console.warn("Could not get port from IPC:", e);
          }
        }

        // Try the IPC-provided port first, then others
        const orderedPorts = [expressPort, ...portsToTry.filter(p => p !== expressPort)];

        for (const port of orderedPorts) {
          try {
            console.log(`Trying to fetch server-info from port ${port}...`);
            const response = await axios.get(`http://localhost:${port}/api/server-info`, {
              timeout: 3000,
            });
            if (response.data && response.data.addresses) {
              this.serverAddresses = response.data.addresses;
              console.log(`Successfully loaded server IPs from port ${port}:`, this.serverAddresses);
              localStorage.setItem("express_port", port.toString());
              break;
            }
          } catch (e) {
            console.warn(`Port ${port} failed:`, e.message);
          }
        }
      } catch (e) {
        console.error("Failed to load server IPs:", e.message || e);
      }

      this.loadingIPs = false;
    },
    async testConnection() {
      this.testing = true;
      this.testResult = null;

      try {
        // Use IPC to test connection from main process
        if (window.electron) {
          const result = await window.electron.testConnection(this.masterIP);
          if (result.success && result.port) {
            this.masterPort = result.port;
            console.log(`[SETUP] Master found on port ${result.port}`);
          }
          this.testResult = {
            success: result.success,
            message: result.success
              ? `Connected successfully to ${result.data?.hostname || this.masterIP} on port ${result.port || 8080}`
              : `Connection failed: ${result.error}`,
          };
        } else {
          // Fallback for web testing (shouldn't happen in Electron)
          this.testResult = {
            success: false,
            message: "Setup wizard is only available in desktop mode",
          };
        }
      } catch (e) {
        this.testResult = {
          success: false,
          message: `Connection failed: ${e.message}`,
        };
      }

      this.testing = false;
    },

    async startDownload() {
      this.saving = true;

      try {
        // Save settings first
        localStorage.setItem("app_mode", this.appMode);
        if (this.appMode === "client") {
          localStorage.setItem("master_ip", this.masterIP);
          localStorage.setItem("master_port", this.masterPort.toString());
          console.log(`[SETUP] Saved master_port: ${this.masterPort}`);
        }

        if (window.electron) {
          await window.electron.setAppMode(this.appMode);
          if (this.appMode === "client") {
            await window.electron.setMasterIP(this.masterIP);
          }
        }

        this.saving = false;
        this.step = 4;

        // Start the download process
        await this.performDownload();
      } catch (e) {
        console.error("Failed to start download:", e);
        this.saving = false;
        this.downloadError = e.message;
      }
    },

    async performDownload() {
      this.downloadError = null;
      this.downloadComplete = false;
      this.downloadStatus = "Preparing to download media files...";
      this.downloadStartTime = Date.now();
      this.totalBytesDownloaded = 0;

      try {
        // Note: JSON data is already synced during build/serve
        // Setup Wizard only downloads media assets (videos, PDFs, images)
        this.downloadStatus = "Downloading course media files...";
        this.overallProgress = 5;

        // Download assets (no auth required - downloads from Vultr CDN)
        if (window.electron && window.electron.downloadAssetsWithProgress) {
          // Use IPC with progress callback
          await this.downloadAssetsWithProgress();
        } else {
          // Fallback: trigger sync via API
          await this.downloadAssetsFallback();
        }

        this.downloadComplete = true;
        this.overallProgress = 100;
        this.downloadStatus = "All media files downloaded successfully!";

      } catch (e) {
        console.error("Download failed:", e);
        this.downloadError = e.message || "Download failed. Please check your internet connection.";
        this.downloadSkippable = true;
      }
    },

    async downloadAssetsWithProgress() {
      // Clean up any existing listeners first
      if (this.progressCleanup) {
        this.progressCleanup();
        this.progressCleanup = null;
      }
      if (this.completeCleanup) {
        this.completeCleanup();
        this.completeCleanup = null;
      }

      // Return a promise that resolves when download completes
      return new Promise((resolve, reject) => {
        // Listen for progress updates from main process
        if (window.electron && window.electron.onDownloadProgress) {
          this.progressCleanup = window.electron.onDownloadProgress((progress) => {
            // Only update if progress moves forward (prevent fluctuation)
            const newDownloaded = progress.downloaded || 0;
            const newTotal = progress.total || 0;

            // Always accept if total changes or downloaded increases
            if (newTotal !== this.totalFiles || newDownloaded >= this.downloadedFiles) {
              this.totalFiles = newTotal;
              this.downloadedFiles = newDownloaded;
              this.currentFileName = progress.currentFile || "";
              this.currentFileProgress = progress.fileProgress || 0;

              // Only update bytes if it increases (prevents fluctuation)
              const newBytes = progress.bytesDownloaded || 0;
              if (newBytes >= this.totalBytesDownloaded) {
                this.totalBytesDownloaded = newBytes;
                this.downloadedMB = (this.totalBytesDownloaded / (1024 * 1024)).toFixed(1);
              }

              // Calculate overall progress (20-100%)
              if (this.totalFiles > 0) {
                const newProgress = 20 + Math.round((this.downloadedFiles / this.totalFiles) * 80);
                // Only update if progress increases
                if (newProgress >= this.overallProgress) {
                  this.overallProgress = newProgress;
                }
              }

              // Calculate speed
              const elapsedSeconds = (Date.now() - this.downloadStartTime) / 1000;
              if (elapsedSeconds > 0) {
                const speedMBps = (this.totalBytesDownloaded / (1024 * 1024)) / elapsedSeconds;
                this.downloadSpeed = speedMBps > 1
                  ? `${speedMBps.toFixed(1)} MB/s`
                  : `${(speedMBps * 1024).toFixed(0)} KB/s`;
              }

              this.downloadStatus = progress.status || `Downloading ${this.currentFileName}...`;
            }
          });
        }

        // Listen for download completion event
        if (window.electron && window.electron.onDownloadComplete) {
          this.completeCleanup = window.electron.onDownloadComplete((result) => {
            // Clean up listeners
            if (this.progressCleanup) {
              this.progressCleanup();
              this.progressCleanup = null;
            }
            if (this.completeCleanup) {
              this.completeCleanup();
              this.completeCleanup = null;
            }

            if (result.success) {
              console.log("Download completed:", result);
              resolve(result);
            } else {
              console.error("Download failed:", result.error);
              reject(new Error(result.error || "Asset download failed"));
            }
          });
        }

        // Start the download (returns immediately, download runs in background)
        window.electron.downloadAssets()
          .then(async (startResult) => {
            console.log("Download started:", startResult);
            if (startResult.inProgress) {
              // Download already in progress (e.g., page was reloaded)
              // Fetch current status and keep listeners attached
              console.log("Download already in progress, reconnecting...");
              this.downloadStatus = "Reconnecting to download in progress...";

              // Get current download status
              if (window.electron.downloadStatus) {
                try {
                  const status = await window.electron.downloadStatus();
                  console.log("Current download status:", status);
                  if (status && status.status) {
                    this.totalFiles = status.status.totalAssets || 0;
                    this.downloadedFiles = status.status.downloadedAssets || 0;
                    if (this.totalFiles > 0) {
                      this.overallProgress = 20 + Math.round((this.downloadedFiles / this.totalFiles) * 80);
                    }
                    this.downloadStatus = `Downloading... ${this.downloadedFiles}/${this.totalFiles} files`;
                  }
                } catch (e) {
                  console.warn("Failed to get download status:", e);
                }
              }
              return;
            }
            if (!startResult.success && !startResult.started) {
              // Failed to even start the download
              if (this.progressCleanup) {
                this.progressCleanup();
                this.progressCleanup = null;
              }
              if (this.completeCleanup) {
                this.completeCleanup();
                this.completeCleanup = null;
              }
              reject(new Error(startResult.error || "Failed to start download"));
            }
            // If started successfully, wait for download-complete event
          })
          .catch((err) => {
            if (this.progressCleanup) {
              this.progressCleanup();
              this.progressCleanup = null;
            }
            if (this.completeCleanup) {
              this.completeCleanup();
              this.completeCleanup = null;
            }
            reject(err);
          });
      });
    },

    async downloadAssetsFallback() {
      // Fallback method using API endpoint
      this.downloadStatus = "Downloading assets...";

      try {
        const response = await axios.post("/sync/assets", {}, { timeout: 600000 });
        if (response.data && response.data.success) {
          this.downloadedFiles = response.data.downloaded || 0;
          this.totalFiles = response.data.total || 0;
          this.overallProgress = 100;
        }
      } catch (e) {
        console.warn("Asset sync via API failed:", e);
        // Don't throw - assets can be downloaded later
        this.downloadSkippable = true;
      }
    },

    async retryDownload() {
      this.downloadError = null;
      this.downloadSkippable = false;
      await this.performDownload();
    },

    async skipDownload() {
      // Mark setup as complete but skip asset download
      localStorage.setItem("setup_complete", "true");
      localStorage.setItem("assets_pending", "true");

      if (window.electron) {
        await window.electron.setSetupComplete(true);
      }

      window.location.reload();
    },

    async completeSetup() {
      this.saving = true;

      try {
        // Save to localStorage (for axios-offline.js to read on startup)
        localStorage.setItem("app_mode", this.appMode);
        localStorage.setItem("setup_complete", "true");
        localStorage.removeItem("assets_pending");

        if (this.appMode === "client") {
          localStorage.setItem("master_ip", this.masterIP);
          localStorage.setItem("master_port", this.masterPort.toString());
          console.log(`[SETUP] Saved master_port in completeSetup: ${this.masterPort}`);
        }

        // Also save to SQLite via IPC (for persistence)
        if (window.electron) {
          await window.electron.setAppMode(this.appMode);
          if (this.appMode === "client") {
            await window.electron.setMasterIP(this.masterIP);
          }
          await window.electron.setSetupComplete(true);
        }

        this.saving = false;

        // Reload app to apply settings
        window.location.reload();
      } catch (e) {
        console.error("Setup failed:", e);
        this.saving = false;
      }
    },
  },
};
</script>
